import { ContactDeliveryAddress } from 'src/app/core/models/contact/contact-delivery-address.interface';
import { ContactDeliveryCarrier } from 'src/app/core/models/contact/contact-delivery-carrier.type';
import { ContactDeliveryAddressCardViewModel } from './contact-delivery-address-card-view-model.interface';

type DeliveryAddressFormValue = {
  label: string | null;
  kind: 'address' | 'branch';
  isPrimary: boolean;
  country: string | null;
  city: string | null;
  street: string | null;
  postalCode: string | null;
  carrier: ContactDeliveryCarrier | null;
  carrierName: string | null;
  branchCity: string | null;
  branchNumber: string | null;
  recipientName: string | null;
  recipientPhone: string | null;
  notes: string | null;
};

export function cloneDeliveryAddresses(
  deliveryAddresses: ContactDeliveryAddress[],
): ContactDeliveryAddress[] {
  return deliveryAddresses.map((address) => ({ ...address }));
}

export function mapFormValueToDeliveryAddress(
  formValue: DeliveryAddressFormValue,
  addressId: string,
): ContactDeliveryAddress {
  if (formValue.kind === 'address') {
    return {
      id: addressId,
      kind: 'address',
      isPrimary: formValue.isPrimary,
      label: trimOrNull(formValue.label),
      country: trimOrNull(formValue.country),
      city: trimOrNull(formValue.city),
      street: trimOrNull(formValue.street),
      postalCode: trimOrNull(formValue.postalCode),
      carrier: null,
      carrierName: null,
      branchCity: null,
      branchNumber: null,
      recipientName: trimOrNull(formValue.recipientName),
      recipientPhone: trimOrNull(formValue.recipientPhone),
      notes: trimOrNull(formValue.notes),
    };
  }

  return {
    id: addressId,
    kind: 'branch',
    isPrimary: formValue.isPrimary,
    label: trimOrNull(formValue.label),
    carrier: formValue.carrier,
    carrierName: formValue.carrier === 'other'
      ? trimOrNull(formValue.carrierName)
      : null,
    branchCity: trimOrNull(formValue.branchCity),
    branchNumber: trimOrNull(formValue.branchNumber),
    country: null,
    city: null,
    street: null,
    postalCode: null,
    recipientName: trimOrNull(formValue.recipientName),
    recipientPhone: trimOrNull(formValue.recipientPhone),
    notes: trimOrNull(formValue.notes),
  };
}

export function normalizeDeliveryAddresses(
  deliveryAddresses: ContactDeliveryAddress[],
): ContactDeliveryAddress[] {
  const normalizedAddresses = deliveryAddresses.map((address, index) => {
    if (address.kind === 'address') {
      return {
        ...address,
        sortOrder: index,
        carrier: null,
        carrierName: null,
        branchCity: null,
        branchNumber: null,
      };
    }

    return {
      ...address,
      sortOrder: index,
      country: null,
      city: null,
      street: null,
      postalCode: null,
    };
  });

  if (normalizedAddresses.length === 0) {
    return normalizedAddresses;
  }

  const primaryIndex = normalizedAddresses.findIndex((address) => address.isPrimary);
  const normalizedPrimaryIndex = primaryIndex >= 0 ? primaryIndex : 0;

  return normalizedAddresses.map((address, index) => ({
    ...address,
    isPrimary: index === normalizedPrimaryIndex,
  }));
}

export function buildDeliveryAddressCards(
  deliveryAddresses: ContactDeliveryAddress[],
): ContactDeliveryAddressCardViewModel[] {
  return deliveryAddresses
    .slice()
    .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
    .map((address) => ({
      id: address.id,
      kindLabelKey:
        address.kind === 'branch'
          ? 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.TYPES.BRANCH'
          : 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.TYPES.ADDRESS',
      kindBadgeClass:
        address.kind === 'branch'
          ? 'contact-delivery-addresses__kind-badge contact-delivery-addresses__kind-badge--branch'
          : 'contact-delivery-addresses__kind-badge contact-delivery-addresses__kind-badge--address',
      carrierLabel: address.kind === 'branch' ? getCarrierLabel(address) : null,
      iconShellClass:
        address.kind === 'branch'
          ? 'contact-delivery-addresses__type-icon contact-delivery-addresses__type-icon--branch'
          : 'contact-delivery-addresses__type-icon contact-delivery-addresses__type-icon--address',
      typeIcon: address.kind === 'branch' ? 'inbox' : 'truck',
      label: trimOrNull(address.label),
      summary: address.kind === 'branch'
        ? [
            trimOrNull(address.branchCity),
            getCarrierLabel(address),
            trimOrNull(address.branchNumber),
          ]
            .filter((value): value is string => !!value)
            .join(', ')
        : [
            trimOrNull(address.country),
            trimOrNull(address.city),
            trimOrNull(address.street),
            trimOrNull(address.postalCode),
          ]
            .filter((value): value is string => !!value)
            .join(', '),
      recipientName: trimOrNull(address.recipientName),
      recipientPhone: trimOrNull(address.recipientPhone),
      notes: trimOrNull(address.notes),
      isPrimary: address.isPrimary,
    }));
}

function getCarrierLabel(address: ContactDeliveryAddress): string | null {
  switch (address.carrier) {
    case 'nova-poshta':
      return 'Nova Poshta';
    case 'ukrposhta':
      return 'Ukrposhta';
    case 'meest':
      return 'Meest';
    case 'other':
      return trimOrNull(address.carrierName);
    default:
      return null;
  }
}

function trimOrNull(value: string | null | undefined): string | null {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : null;
}
